import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useRef } from "react";
import classNames from "classnames";
import { createRoot } from "react-dom/client";
import useElementBounds from "../../lib/hooks/use-element-bounds";
import useDocumentSize from "../../lib/hooks/use-document-size";
import useOnClickOutside from "../../lib/hooks/use-on-click-outside";
import style from "./style.module.scss";
export var TooltipPosition;
(function (TooltipPosition) {
    TooltipPosition["Above"] = "above";
    TooltipPosition["Below"] = "below";
    TooltipPosition["Left"] = "left";
    TooltipPosition["Right"] = "right";
})(TooltipPosition || (TooltipPosition = {}));
/**
 * TooltipContainer is the "behind the scenes" component that provides the positioning and general
 * tooltip behaviour.
 */
export const TooltipContainer = (props) => {
    function unmount() {
        const root = Tooltip.roots[props.containerId];
        if (root) {
            root.unmount();
        }
    }
    const { originX = 0, originY = 0, position = TooltipPosition.Above } = props;
    const originOffset = 30;
    const boundaryOffset = 10;
    const ref = useRef(null);
    const [setElementSizeRef, elementSize] = useElementBounds();
    const docSize = useDocumentSize();
    useOnClickOutside(ref, unmount);
    useLayoutEffect(() => {
        if (ref.current) {
            setElementSizeRef(ref.current);
        }
    }, [ref, setElementSizeRef]);
    useLayoutEffect(() => {
        window.addEventListener("resize", unmount);
        return () => {
            window.removeEventListener("resize", unmount);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    let positionX = originX;
    let positionY = originY;
    switch (position) {
        case TooltipPosition.Above:
            // Centred above the origin
            positionX = originX - elementSize.width / 2;
            positionY = originY - elementSize.height - originOffset;
            break;
        case TooltipPosition.Below:
            // Centred below the origin
            positionX = originX - elementSize.width / 2;
            positionY = originY + originOffset;
            break;
    }
    if (elementSize.width === 0) {
        // During the first render, we don't want the tooltip to be squashed against the document
        // boundary. Rendering the tooltip as far left as possible will allow us to calculate an
        // accurate width. It will be positioned correctly in the next render.
        positionX = 0;
    }
    else {
        // Make sure the tooltip doesn't go out of the window bounds
        // Right boundary
        positionX = Math.min(positionX, docSize.width - elementSize.width - boundaryOffset);
        // Left boundary
        positionX = Math.max(positionX, boundaryOffset);
        // Top boundary
        positionY = Math.max(positionY, boundaryOffset);
        // Bottom boundary
        positionY = Math.min(positionY, docSize.height - elementSize.height - boundaryOffset);
    }
    return (_jsx("div", { ref: ref, className: style["tooltip-container"], style: {
            left: positionX,
            top: positionY,
        }, children: props.children }));
};
/**
 * Tooltip is the user-facing component that provides styling for tooltip content.
 */
const Tooltip = (props) => {
    const { animated = false, className, children, footer, wide = false } = props;
    return (_jsxs("div", { className: classNames(style["tooltip"], className, {
            [style["animated"]]: animated,
            [style["tooltip--wide"]]: wide,
        }), children: [_jsx("div", { className: style["tooltip-body"], children: children }), footer && React.Children.count(footer) > 0 ? (_jsx("div", { className: style["tooltip-footer"], children: footer })) : null] }));
};
Tooltip.DEFAULT_CONTAINER_ID = "speedcurve-tooltip-container";
Tooltip.containers = {};
Tooltip.roots = {};
Tooltip.getContainer = function (containerId) {
    if (!containerId) {
        containerId = this.DEFAULT_CONTAINER_ID;
    }
    if (this.containers[containerId]) {
        return this.containers[containerId];
    }
    let container = document.getElementById(containerId);
    if (!container) {
        container = document.createElement("div");
        container.id = containerId;
        document.body.appendChild(container);
    }
    this.containers[containerId] = container;
    return container;
};
Tooltip.showForMouseEvent = function (event, component, props = {}) {
    // Prevent this event from propagating to the unmount handler
    event.stopPropagation();
    let containerId = props.containerId;
    let container;
    if (containerId) {
        container = this.getContainer(containerId);
    }
    else {
        container = this.getContainer();
        containerId = container.id;
    }
    if (!this.roots[containerId]) {
        this.roots[containerId] = createRoot(container);
    }
    this.roots[containerId].render(_jsx(TooltipContainer, Object.assign({ originX: event.pageX, originY: event.pageY, containerId: containerId }, props, { children: component() })));
};
Tooltip.hide = function (containerId) {
    if (!containerId) {
        containerId = this.DEFAULT_CONTAINER_ID;
    }
    const root = this.roots[containerId];
    if (root) {
        root.unmount();
        delete this.roots[containerId];
    }
};
export default Tooltip;
